import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, Phone, Building2, MapPin, ReceiptText, LifeBuoy, LayoutList, Plus, KeyRound, CalendarClock, User, Pencil, StickyNote } from "lucide-react";
import api from "@/lib/api";
import { dialNumber } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import CustomerEditDialog from "@/components/CustomerEditDialog";
import { money, caseStatusColor, priorityColor } from "@/lib/caseUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import CaseFormDialog from "@/components/CaseFormDialog";
import CaseDetailDialog from "@/components/CaseDetailDialog";
import AddSubscriptionDialog from "@/components/AddSubscriptionDialog";

const fmtAmt = (n, cur) => (cur === "AUD" ? "A$" : "$") + (Number(n) || 0).toLocaleString();

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-sm font-medium mt-0.5 ${mono ? "font-mono" : ""}`}>{value || "—"}</p>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [inv, setInv] = useState({ amount: "", due_date: "", description: "" });
  const [invoices, setInvoices] = useState([]);
  const [cases, setCases] = useState([]);
  const [caseFormOpen, setCaseFormOpen] = useState(false);
  const [openCaseId, setOpenCaseId] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [addSubOpen, setAddSubOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const todayIso = new Date().toISOString().slice(0, 10);

  const load = () => api.get(`/clients/${id}`).then((r) => setC(r.data)).catch(() => setNotFound(true));
  const loadInvoices = () => api.get(`/invoices`).then((r) => setInvoices(r.data.filter((x) => x.client_id === id))).catch(() => {});
  const loadCases = () => api.get(`/cases?client_id=${id}`).then((r) => setCases(r.data)).catch(() => {});
  useEffect(() => { setNotFound(false); setC(null); load(); loadInvoices(); loadCases(); }, [id]);

  if (notFound) return (
    <div className="max-w-md mx-auto text-center py-20 space-y-4" data-testid="client-not-found">
      <p className="text-lg font-heading font-semibold">Customer not found</p>
      <p className="text-sm text-muted-foreground">This record may have been removed or the link is out of date. Try the search bar to find them again.</p>
      <Button className="rounded-sm" onClick={() => navigate("/clients")} data-testid="not-found-back">Back to Customers</Button>
    </div>
  );
  if (!c) return <div className="text-muted-foreground">Loading…</div>;
  const isSale = c.outcome === "Sale";
  const openCount = cases.filter((x) => x.status === "Open" || x.status === "In Progress").length;
  const subs = c.subscriptions || [];
  const subCurrency = subs[0]?.currency || "USD";

  const addNote = async () => {
    if (!noteText.trim()) { toast.error("Write a note first"); return; }
    try {
      const { data } = await api.post(`/clients/${id}/support-notes`, { note: noteText.trim() });
      setC(data); setNoteText(""); toast.success("Note added");
    } catch (e) { toast.error("Could not add note"); }
  };

  const addInvoice = async () => {
    if (!inv.amount) { toast.error("Amount required"); return; }
    try {
      await api.post(`/invoices`, { client_id: id, client_name: c.name, amount: parseFloat(inv.amount), due_date: inv.due_date, description: inv.description, status: "Pending" });
      setInv({ amount: "", due_date: "", description: "" }); loadInvoices(); toast.success("Invoice created");
    } catch (e) { toast.error("Could not create invoice"); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-clients"><ArrowLeft size={15} /> Customers</button>

      <div className="rounded-sm border border-border border-l-4 border-l-primary bg-card p-6 flex flex-wrap items-start gap-4 shadow-sm">
        <div className="h-14 w-14 rounded-sm bg-gradient-to-br from-primary to-primary/70 text-primary-foreground flex items-center justify-center font-heading font-bold text-xl shadow-sm">{c.name.slice(0, 1).toUpperCase()}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-heading font-bold tracking-tight" data-testid="client-detail-name">{c.name}</h1>
            <Badge variant="outline" className={`rounded-sm ${isSale ? "bg-success/15 text-success border-success/30" : "bg-muted text-muted-foreground border-border"}`}>{c.outcome || "—"}</Badge>
            {openCount > 0 && <Badge variant="outline" data-testid="open-cases-badge" className="rounded-sm bg-warning/15 text-warning border-warning/30">{openCount} open case{openCount > 1 ? "s" : ""}</Badge>}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-muted-foreground">
            {c.company && <span className="flex items-center gap-1.5"><Building2 size={14} /> {c.company}</span>}
            {c.phone && <button onClick={() => dialNumber(c.phone)} className="flex items-center gap-1.5 font-mono text-primary hover:opacity-80" data-testid="dial-customer"><Phone size={14} /> {c.phone}</button>}
            {c.email && <span className="flex items-center gap-1.5"><Mail size={14} /> {c.email}</span>}
            {c.address && <span className="flex items-center gap-1.5"><MapPin size={14} /> {c.address}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && <Button variant="outline" className="rounded-sm gap-2" onClick={() => setEditOpen(true)} data-testid="edit-customer-btn"><Pencil size={15} /> Edit</Button>}
          <Button className="rounded-sm gap-2" onClick={() => setCaseFormOpen(true)} data-testid="open-case-btn"><Plus size={16} /> Open Case</Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="rounded-sm">
          <TabsTrigger value="overview" className="rounded-sm gap-1.5" data-testid="tab-overview"><LayoutList size={14} /> Overview</TabsTrigger>
          <TabsTrigger value="cases" className="rounded-sm gap-1.5" data-testid="tab-cases"><LifeBuoy size={14} /> Cases {cases.length > 0 && <span className="ml-1 text-xs bg-secondary rounded px-1.5">{cases.length}</span>}</TabsTrigger>
          <TabsTrigger value="invoices" className="rounded-sm gap-1.5" data-testid="tab-invoices"><ReceiptText size={14} /> Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          {isSale ? (
            <div className="space-y-4">
              <div className="rounded-sm border border-border bg-card p-6">
                <h3 className="font-heading font-semibold mb-4 flex items-center gap-2"><KeyRound size={16} className="text-primary" /> Customer</h3>
                <div className="grid sm:grid-cols-4 gap-5">
                  <Field label="Customer ID" value={c.customer_id} mono />
                  <Field label="Country" value={c.country} />
                  <Field label="Agent" value={c.agent} />
                  <Field label="Total (excl. refunds)" value={fmtAmt(c.amount, subCurrency)} mono />
                </div>
              </div>
              <div className="rounded-sm border border-border bg-card overflow-hidden">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2"><CalendarClock size={16} className="text-primary" /><h3 className="font-heading font-semibold">Subscriptions <span className="text-muted-foreground font-normal">({subs.length})</span></h3></div>
                  <Button size="sm" variant="outline" className="rounded-sm gap-1.5" onClick={() => setAddSubOpen(true)} data-testid="add-subscription-btn"><Plus size={14} /> Add Subscription</Button>
                </div>
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Date</TableHead><TableHead>Antivirus</TableHead><TableHead>License</TableHead><TableHead>Key</TableHead><TableHead>Start → End</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody data-testid="subscriptions-table">
                    {subs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No subscriptions.</TableCell></TableRow>}
                    {subs.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell className="text-sm whitespace-nowrap">{s.date || s.subscription_start || "—"}</TableCell>
                        <TableCell className="text-sm font-medium">{s.av_name || "—"}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{s.license_info || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{s.license_key ? <code className="bg-muted px-1.5 py-0.5 rounded-md">{s.license_key}</code> : "—"}</TableCell>
                        <TableCell className="font-mono text-xs whitespace-nowrap">{s.subscription_start || "—"} → {s.subscription_end || "—"}</TableCell>
                        <TableCell className="text-right font-mono">{fmtAmt(s.amount, s.currency)}</TableCell>
                        <TableCell>{s.refunded ? <Badge variant="destructive" className="rounded-sm">Refunded</Badge> : (s.subscription_end && s.subscription_end < todayIso) ? <Badge variant="outline" className="rounded-sm bg-primary/15 text-primary border-primary/30">Expired</Badge> : <Badge variant="outline" className="rounded-sm bg-success/15 text-success border-success/30">Active</Badge>}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {c.address && <div className="rounded-sm border border-border bg-card p-6"><p className="text-xs text-muted-foreground">Billing Address</p><p className="text-sm mt-1">{c.address}</p></div>}
            </div>
          ) : (
            <div className="rounded-sm border border-border bg-card p-6 text-sm text-muted-foreground">This entry was recorded as <strong className="text-foreground">No Sale</strong>. It stays searchable for future follow-up.</div>
          )}
          <div className="rounded-sm border border-border bg-card p-6" data-testid="notes-section">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><StickyNote size={16} className="text-primary" /> Notes</h3>
            <div className="flex gap-2 mb-4">
              <Input value={noteText} onChange={(e) => setNoteText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add a note (enquiry, follow-up, remark)…" className="rounded-sm" data-testid="note-input" />
              <Button className="rounded-sm gap-1.5" onClick={addNote} data-testid="add-note-btn"><Plus size={15} /> Add</Button>
            </div>
            <div className="space-y-2" data-testid="notes-list">
              {(!c.support_notes || c.support_notes.length === 0) && <p className="text-sm text-muted-foreground">No notes yet.</p>}
              {(c.support_notes || []).slice().reverse().map((n) => (
                <div key={n.id} className="rounded-sm border border-border p-3" data-testid="note-item">
                  <p className="text-sm whitespace-pre-wrap">{n.note}</p>
                  <p className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1.5"><User size={12} /> {n.author || "—"} · {new Date(n.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
          {c.call_notes && (
            <div className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-heading font-semibold mb-2">Call Notes</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{c.call_notes}</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="cases" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Support cases raised over the customer's lifetime — open a new one anytime, close when resolved.</p>
            <Button variant="outline" className="rounded-sm gap-1.5" onClick={() => setCaseFormOpen(true)} data-testid="open-case-btn-2"><Plus size={15} /> Open Case</Button>
          </div>
          <div className="space-y-2" data-testid="client-cases-list">
            {cases.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center border border-border rounded-sm bg-card">No cases yet.</p>}
            {cases.map((k) => (
              <button key={k.id} onClick={() => setOpenCaseId(k.id)} data-testid={`case-item-${k.id}`}
                className="w-full text-left rounded-sm border border-border bg-card p-4 hover:border-primary/50 transition-colors flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{k.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{k.category} · {k.tech_assigned || "unassigned"} · opened {new Date(k.created_at).toLocaleDateString()}</p>
                </div>
                <Badge variant="outline" className={`rounded-sm ${priorityColor[k.priority]}`}>{k.priority}</Badge>
                <Badge variant="outline" className={`rounded-sm ${caseStatusColor[k.status]}`}>{k.status}</Badge>
              </button>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4 space-y-4">
          <div className="rounded-sm border border-border bg-card p-4 grid md:grid-cols-4 gap-2 items-end">
            <div className="grid gap-1.5"><Label>Amount</Label><Input data-testid="inv-amount" type="number" value={inv.amount} onChange={(e) => setInv({ ...inv, amount: e.target.value })} className="rounded-sm font-mono" /></div>
            <div className="grid gap-1.5"><Label>Due date</Label><Input data-testid="inv-due" type="date" value={inv.due_date} onChange={(e) => setInv({ ...inv, due_date: e.target.value })} className="rounded-sm" /></div>
            <div className="grid gap-1.5"><Label>Description</Label><Input data-testid="inv-desc" value={inv.description} onChange={(e) => setInv({ ...inv, description: e.target.value })} className="rounded-sm" /></div>
            <Button className="rounded-sm gap-1.5" onClick={addInvoice} data-testid="add-invoice-btn"><Plus size={15} /> Invoice</Button>
          </div>
          <div className="rounded-sm border border-border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Invoice #</TableHead><TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody data-testid="client-invoices-table">
                {invoices.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No invoices for this customer.</TableCell></TableRow>}
                {invoices.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono text-sm">{i.invoice_no}</TableCell>
                    <TableCell className="text-sm">{i.due_date || "—"}</TableCell>
                    <TableCell><Badge variant={i.status === "Overdue" ? "destructive" : "outline"} className="rounded-sm">{i.status}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{money(i.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      <CaseFormDialog open={caseFormOpen} onOpenChange={setCaseFormOpen} clientId={id} onSaved={loadCases} />
      <CaseDetailDialog open={!!openCaseId} onOpenChange={(v) => !v && setOpenCaseId(null)} caseId={openCaseId} onChanged={loadCases} />
      {isAdmin && <CustomerEditDialog open={editOpen} onOpenChange={setEditOpen} client={c} onSaved={load} />}
      <AddSubscriptionDialog open={addSubOpen} onOpenChange={setAddSubOpen} clientId={id} onSaved={(data) => { if (data) setC(data); else load(); }} />
    </div>
  );
}
